import { Copy, KeyRound, LogOut, RefreshCw, ShieldCheck, UserRoundCheck, UserRoundX } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import {
  createSetupLink,
  loadAccessUsers,
  revokeAccessSessions,
  setAccessUserEnabled,
  type AccessUser,
} from './access-api';
import './owner-pages.css';

function when(value: string | null): string {
  if (!value) return 'Never';
  const date = new Date(value);
  return Number.isFinite(date.getTime()) ? date.toLocaleString('en-GB') : '—';
}

export function AccessPage() {
  const [users, setUsers] = useState<AccessUser[] | null>(null);
  const [setupUrl, setSetupUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');

  const refresh = useCallback(async () => {
    try {
      setUsers((await loadAccessUsers()).users);
      setMessage('');
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Access could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  async function setup(user: AccessUser) {
    setBusy(true);
    setMessage('');
    try {
      const result = await createSetupLink(user.username);
      setSetupUrl(result.setupUrl);
      await refresh();
      setMessage(`One-time password setup link created for ${user.username}; expires in ${result.expiresInHours} hours.`);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Setup link could not be created.');
    } finally {
      setBusy(false);
    }
  }

  async function copySetupLink() {
    if (!setupUrl) return;
    try {
      await navigator.clipboard.writeText(setupUrl);
      setMessage('One-time setup link copied to the clipboard.');
    } catch {
      setMessage('The setup link could not be copied. Select the link above and copy it manually.');
    }
  }

  async function toggle(user: AccessUser) {
    if (!window.confirm(`${user.enabled ? 'Disable' : 'Enable'} ${user.username}?${user.enabled ? ' Existing sessions will be revoked.' : ''}`)) return;
    setBusy(true);
    setMessage('');
    try {
      await setAccessUserEnabled(user.username, !user.enabled);
      await refresh();
      setMessage(`${user.username} ${user.enabled ? 'disabled' : 'enabled'}.`);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Account change failed.');
    } finally {
      setBusy(false);
    }
  }

  async function revoke(user: AccessUser) {
    if (!window.confirm(`Sign ${user.username} out of every FFSControl session?`)) return;
    setBusy(true);
    setMessage('');
    try {
      const result = await revokeAccessSessions(user.username);
      setMessage(`${result.sessionsRevoked} session${result.sessionsRevoked === 1 ? '' : 's'} revoked for ${user.username}.`);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Sessions could not be revoked.');
    } finally {
      setBusy(false);
    }
  }

  if (loading) return <div className="ffs-next-loading">Loading Access &amp; Security…</div>;

  if (!users) {
    return (
      <div className="ffs-workspace-page ffs-owner-page">
        <div className="ffs-workspace-message" aria-live="polite">{message || 'Access could not be loaded.'}</div>
        <section className="ffs-workspace-hero">
          <div><small>OWNER ONLY</small><h2>Access &amp; Security</h2><p>Operator-account data is unavailable. No account state is being inferred.</p></div>
          <button type="button" className="ffs-button secondary" onClick={() => void refresh()}><RefreshCw size={14} /> Retry</button>
        </section>
      </div>
    );
  }

  return (
    <div className="ffs-workspace-page ffs-owner-page">
      {message ? <div className="ffs-workspace-message" aria-live="polite">{message}</div> : null}

      <section className="ffs-workspace-hero">
        <div>
          <small>OWNER ONLY</small>
          <h2>Access &amp; Security</h2>
          <p>Manage operator access without exposing passwords, owner authentication secrets or session tokens.</p>
        </div>
        <button type="button" className="ffs-button secondary" disabled={busy} onClick={() => void refresh()}>
          <RefreshCw size={14} /> Refresh
        </button>
      </section>

      {setupUrl ? (
        <section className="ffs-owner-secret">
          <strong>One-time operator setup link</strong>
          <p>Share this only with the intended operator. The server invalidates previous unused setup links for that account.</p>
          <code>{setupUrl}</code>
          <button type="button" className="ffs-button primary" onClick={() => void copySetupLink()}>
            <Copy size={14} /> Copy Link
          </button>
          <button type="button" className="ffs-button secondary" onClick={() => setSetupUrl('')}>Hide</button>
        </section>
      ) : null}

      <section className="ffs-access-grid">
        {users.length === 0 ? (
          <article className="ffs-workspace-card"><div className="ffs-empty-state">No operator accounts are configured.</div></article>
        ) : users.map((user) => (
          <article className="ffs-workspace-card ffs-access-user" key={user.username}>
            <div className="ffs-access-user-head">
              <div><h3>{user.username}</h3><small>Operator account</small></div>
              <span>{user.enabled ? 'ENABLED' : 'DISABLED'}</span>
            </div>
            <div className="ffs-access-facts">
              <div><span>Password</span><strong>{user.passwordConfigured ? 'Set' : 'Setup required'}</strong></div>
              <div><span>Last login</span><strong>{when(user.lastLoginAt)}</strong></div>
              <div><span>Created</span><strong>{when(user.createdAt)}</strong></div>
            </div>
            <div className="ffs-inline-actions">
              <button type="button" className="ffs-button secondary" disabled={busy} onClick={() => void setup(user)}>
                <KeyRound size={14} /> Generate Setup Link
              </button>
              <button type="button" className="ffs-button warning" disabled={busy} onClick={() => void revoke(user)}>
                <LogOut size={14} /> Sign Out Everywhere
              </button>
              <button type="button" className={`ffs-button ${user.enabled ? 'danger' : 'primary'}`} disabled={busy} onClick={() => void toggle(user)}>
                {user.enabled ? <UserRoundX size={14} /> : <UserRoundCheck size={14} />}
                {user.enabled ? 'Disable Account' : 'Enable Account'}
              </button>
            </div>
          </article>
        ))}
      </section>

      <section className="ffs-workspace-card">
        <header><div><small>SECURITY BOUNDARY</small><h3>What this page can manage</h3></div><ShieldCheck size={18} /></header>
        <div className="ffs-access-note">Operator accounts do not expose or change the owner password, TOTP secret, recovery codes, API credentials or other users’ private credentials. Setup links are one-time server-generated tokens and full passwords are never displayed here.</div>
      </section>
    </div>
  );
}
